"use client";

import Image from "next/image";
import { CircleCheck, Workflow } from "lucide-react";
import { useEffect, useRef, type CSSProperties } from "react";

import { CcFortnoxVideo } from "@/components/services/ai-automation/fortnox/CcFortnoxVideo";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";

import type { SiSubpageCopy } from "./si-page-copy";

type CcSiHeroStageProps = {
  video: SiSubpageCopy["hero"]["video"];
  network: SiSubpageCopy["hero"]["network"];
};

/**
 * The hero's video (cartage.ai) with a network of systems beside the person
 * in it. As the stage scrolls in, the systems travel out from the hub to
 * their places and the connections light up, so the network assembles
 * itself. The scroll handler only writes one CSS variable.
 */
export function CcSiHeroStage({ video, network }: CcSiHeroStageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const reduced = useCcPrefersReducedMotion();

  useEffect(() => {
    const root = rootRef.current;
    if (!root || reduced) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const rect = root.getBoundingClientRect();
      const progress = (window.innerHeight * 1.05 - rect.top) / (window.innerHeight * 0.7);
      root.style.setProperty("--cc-si-p", Math.min(1, Math.max(0, progress)).toFixed(3));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [reduced]);

  const count = network.nodes.length;
  // The network sits beside the person in the clip: to the right on wide
  // screens, below them on phones. Positions are % of the frame.
  const layouts = {
    wide: { cx: 75, cy: 50, rx: 18.5, ry: 36 },
    narrow: { cx: 50, cy: 68, rx: 38, ry: 21 },
  };
  const spotsFor = ({ cx, cy, rx, ry }: (typeof layouts)["wide"]) =>
    network.nodes.map((_, index) => {
      const angle = (index / count) * Math.PI * 2 - Math.PI / 2;
      return { x: cx + Math.cos(angle) * rx, y: cy + Math.sin(angle) * ry };
    });
  const wide = spotsFor(layouts.wide);
  const narrow = spotsFor(layouts.narrow);

  return (
    <div ref={rootRef} className="cc-si-stage" data-reduced={reduced ? "true" : undefined}>
      <div className="cc-si-stage__frame">
        <CcFortnoxVideo
          src={video.src}
          srcSmall={video.srcSmall}
          poster={video.poster}
          className="cc-si-stage__video"
          sizes="(min-width: 1472px) 1472px, 100vw"
          priority
        />
        <span className="cc-si-stage__shade" aria-hidden="true" />

        <div className="cc-si-net" aria-hidden="true">
          {(["wide", "narrow"] as const).map((layout) => {
            const center = layouts[layout];
            const spots = layout === "wide" ? wide : narrow;
            return (
              <svg
                key={layout}
                className={`cc-si-net__lines cc-si-net__lines--${layout}`}
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
              >
                {spots.map((spot, index) => (
                  <line
                    key={network.nodes[index].id}
                    x1={center.cx}
                    y1={center.cy}
                    x2={spot.x.toFixed(2)}
                    y2={spot.y.toFixed(2)}
                  />
                ))}
              </svg>
            );
          })}
          <span
            className="cc-si-net__hub"
            style={
              {
                "--cc-si-cx-w": `${layouts.wide.cx}%`,
                "--cc-si-cy-w": `${layouts.wide.cy}%`,
                "--cc-si-cx-n": `${layouts.narrow.cx}%`,
                "--cc-si-cy-n": `${layouts.narrow.cy}%`,
              } as CSSProperties
            }
          >
            <Workflow size={18} strokeWidth={2.2} />
            {network.hub}
          </span>
          {network.nodes.map((node, index) => (
            <span
              key={node.id}
              className="cc-si-net__node"
              style={
                {
                  "--cc-si-cx-w": `${layouts.wide.cx}%`,
                  "--cc-si-cy-w": `${layouts.wide.cy}%`,
                  "--cc-si-x-w": `${wide[index].x - layouts.wide.cx}%`,
                  "--cc-si-y-w": `${wide[index].y - layouts.wide.cy}%`,
                  "--cc-si-cx-n": `${layouts.narrow.cx}%`,
                  "--cc-si-cy-n": `${layouts.narrow.cy}%`,
                  "--cc-si-x-n": `${narrow[index].x - layouts.narrow.cx}%`,
                  "--cc-si-y-n": `${narrow[index].y - layouts.narrow.cy}%`,
                } as CSSProperties
              }
            >
              <Image src={node.logo} alt="" width={20} height={20} unoptimized loading="eager" />
              <b>{node.name}</b>
            </span>
          ))}
        </div>

        <div className="cc-si-status" aria-hidden="true">
          <span className="cc-si-status__dot" />
          <span>
            <strong>{network.status}</strong>
            <small>
              <CircleCheck size={11} strokeWidth={2.6} />
              {network.updated}
            </small>
          </span>
        </div>
      </div>
    </div>
  );
}
